import imageCompression from 'browser-image-compression'

export type ImageDimensions = { height: number; width: number }

export function getImageUrlDimensionOrThrow(blobUrl: string) {
  return new Promise<ImageDimensions>((resolve, reject) => {
    const el = new Image()
    el.onload = () => resolve({ height: el.height, width: el.width })
    el.onerror = reject
    el.src = blobUrl
  })
}

export async function getImageUrlDimension(blobUrl: string) {
  try {
    return await getImageUrlDimensionOrThrow(blobUrl)
  } catch {
    return undefined
  }
}

export async function getImageFileDimension(file: File) {
  const fileUrl = URL.createObjectURL(file)
  try {
    return await getImageUrlDimension(fileUrl)
  } finally {
    URL.revokeObjectURL(fileUrl)
  }
}

/**
 * Get compressed dimension (<= maxEdge)
 * default maxEdge is `1024`
 * default divisibleBy is `1`
 * @param dim
 * @returns
 */
export const getCompressedDimensions = (dim?: {
  height?: number
  width?: number
  divisibleBy?: number
  maxEdge?: number
}) => {
  if (!dim) return { height: undefined, width: undefined }

  const { height, width } = dim
  if (width == null || height == null || isNaN(width) || isNaN(height)) {
    return { height: undefined, width: undefined }
  }

  const divisibleBy = dim.divisibleBy ?? 1
  const maxEdge = dim.maxEdge ?? 1024

  const maxDim = Math.max(height, width)
  const minDim = Math.min(height, width)

  const compressRatio = maxDim > maxEdge ? maxEdge / maxDim : 1

  const compressedMaxDim =
    Math.floor((maxDim * compressRatio) / divisibleBy) * divisibleBy
  const compressedMinDim =
    Math.floor((minDim * compressRatio) / divisibleBy) * divisibleBy

  if (height > width)
    return { height: compressedMaxDim, width: compressedMinDim }
  return { width: compressedMaxDim, height: compressedMinDim }
}

type ImageCompressionOption = Parameters<typeof imageCompression>[1]

export const compressImage = async (
  file: File,
  opt?: ImageCompressionOption,
) => {
  return await imageCompression.drawFileInCanvas(
    await imageCompression(file, {
      maxWidthOrHeight: 1024,
      maxSizeMB: 1,
      useWebWorker: true,
      fileType: 'image/png',
      ...opt,
    }),
  )
}

export const resizeImage = async (
  file: File,
  width: number,
  height: number,
) => {
  const [image] = await compressImage(file)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  canvas.hidden = true
  const ctx = canvas.getContext('2d', {
    alpha: false,
  })
  if (!ctx) throw new Error('Failed to get context of canvas')
  ctx.drawImage(image, 0, 0, width, height)
  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve))
  if (!blob) throw new Error('Failed to convert canvas to blob')
  return new File([blob], file.name, {
    type: 'image/png',
  })
}

export const isOffscreenCanvas = (o: any): o is OffscreenCanvas =>
  typeof window.OffscreenCanvas === 'function' &&
  o instanceof window.OffscreenCanvas

export const canvasToFile = async (
  canvas: HTMLCanvasElement | OffscreenCanvas,
  filename: string,
) => {
  const blob = isOffscreenCanvas(canvas)
    ? await canvas.convertToBlob()
    : await new Promise<Blob | null>(resolve => {
        canvas.toBlob(resolve)
      })
  if (!blob) return
  const file = new File([blob], filename, {
    type: 'image/png',
  })
  return file
}

export const canvasToBlob = (canvas: HTMLCanvasElement | OffscreenCanvas) => {
  return isOffscreenCanvas(canvas)
    ? canvas.convertToBlob()
    : new Promise<Blob | null>(resolve => {
        canvas.toBlob(resolve)
      })
}

export function dataURIToBlob(dataURI: string) {
  const [, type, encoded] =
    dataURI.match(/^data:(image\/\w+);base64,(.+)$/) ?? []
  if (!type || !encoded) {
    throw new Error('Invalid data uri')
  }

  const byteStr = atob(encoded)
  const arrBuf = new ArrayBuffer(byteStr.length)
  const bufView = new Uint8Array(arrBuf)
  for (let i = 0; i < byteStr.length; i++) {
    bufView[i] = byteStr.charCodeAt(i)
  }
  return new Blob([arrBuf], { type })
}
